// File Security Rules for DealRooms
// Centralized rules for file validation and security

/**
 * File types that are BLOCKED (never allowed)
 */
export const BLOCKED_FILE_EXTENSIONS = [
  // Windows executables
  'exe', 'msi', 'bat', 'cmd', 'com', 'scr', 'pif',
  // macOS executables
  'dmg', 'pkg', 'app',
  // Linux packages
  'deb', 'rpm',
  // Scripts
  'sh', 'bin', 'ps1', 'vbs', 'jar',
  // System files
  'dll', 'sys', 'drv', 'ocx',
  // Dangerous extensions
  'js', 'jsx' // JavaScript files - can be risky if executed
];

/**
 * File types that are ALLOWED but require caution
 */
export const CAUTION_FILE_EXTENSIONS = [
  'zip', // Allow but scan contents
  'rar', '7z', // Archive files - scan contents
  'svg' // Can contain scripts - consider converting to PNG
];

/**
 * File types that are SAFE and ALLOWED
 */
export const ALLOWED_FILE_EXTENSIONS = [
  // Documents
  'pdf',
  'doc', 'docx', // Word
  'xls', 'xlsx', // Excel
  'ppt', 'pptx', // PowerPoint
  'txt', 'csv', 'md', 'rtf', // Text files
  
  // Images
  'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tiff',
  
  // Archives (with scanning)
  'zip', 'tar', 'gz', 'tar.gz',
  
  // Videos (optional)
  'mp4', 'avi', 'mov', 'wmv',
  
  // Audio (optional)
  'mp3', 'wav', 'ogg'
];

/**
 * MIME types that are ALLOWED
 */
export const ALLOWED_MIME_TYPES = [
  // Documents
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/csv',
  'text/markdown',
  'application/rtf',
  
  // Images
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/tiff',
  
  // Archives
  'application/zip',
  'application/x-tar',
  'application/gzip',
  'application/x-compressed',
  
  // Videos
  'video/mp4',
  'video/x-msvideo', // AVI
  'video/quicktime', // MOV
  'video/x-ms-wmv', // WMV
  
  // Audio
  'audio/mpeg', // MP3
  'audio/wav',
  'audio/ogg'
];

/**
 * Maximum file size (100MB default)
 */
export const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

/**
 * Check if file extension is blocked
 */
export const isBlockedExtension = (filename) => {
  const extension = filename.split('.').pop()?.toLowerCase();
  return BLOCKED_FILE_EXTENSIONS.includes(extension);
};

/**
 * Check if file extension requires caution
 */
export const requiresCaution = (filename) => {
  const extension = filename.split('.').pop()?.toLowerCase();
  return CAUTION_FILE_EXTENSIONS.includes(extension);
};

/**
 * Check if file extension is allowed
 */
export const isAllowedExtension = (filename) => {
  const extension = filename.split('.').pop()?.toLowerCase();
  return ALLOWED_FILE_EXTENSIONS.includes(extension);
};

/**
 * Get file extension from filename
 */
export const getFileExtension = (filename) => {
  return filename.split('.').pop()?.toLowerCase() || '';
};

/**
 * Validate file against security rules
 */
export const validateFileSecurity = (file) => {
  const errors = [];
  const warnings = [];
  
  // Check file extension
  const extension = getFileExtension(file.name);
  
  if (!extension) {
    errors.push('File must have an extension');
  } else if (isBlockedExtension(file.name)) {
    // Create a formatted list of allowed file types
    const allowedTypes = ALLOWED_FILE_EXTENSIONS.map(ext => `.${ext}`).join(', ');
    errors.push(`FILE NOT ALLOWED. Upload a [${allowedTypes}] file. Executable files like .${extension.toUpperCase()} are blocked for security reasons.`);
  } else if (requiresCaution(file.name)) {
    warnings.push(`File type "${extension.toUpperCase()}" requires additional scanning`);
  } else if (!isAllowedExtension(file.name)) {
    errors.push(`File type "${extension.toUpperCase()}" is not supported`);
  }
  
  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    errors.push(`File size (${formatFileSize(file.size)}) exceeds maximum allowed size of ${formatFileSize(MAX_FILE_SIZE)}`);
  }
  
  // Check MIME type matches extension (basic validation)
  if (file.type && !ALLOWED_MIME_TYPES.includes(file.type)) {
    warnings.push(`File MIME type "${file.type}" may not match expected type for extension "${extension}"`);
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Get file security rules configuration (for display)
 */
export const getFileSecurityRules = () => {
  return {
    blockedExtensions: BLOCKED_FILE_EXTENSIONS,
    cautionExtensions: CAUTION_FILE_EXTENSIONS,
    allowedExtensions: ALLOWED_FILE_EXTENSIONS,
    maxFileSize: MAX_FILE_SIZE,
    maxFileSizeFormatted: formatFileSize(MAX_FILE_SIZE)
  };
};

